/**
 * ============================================================================
 * Iron Meridian - Combat Feedback Effects
 * ============================================================================
 * 
 * Visual feedback effects for combat outcomes.
 * Short, non-blocking animations (300-600ms) for:
 * - Victory: Gold pulse
 * - Defeat: Red desaturation pulse
 * - Territory capture/loss: Flash effects
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'

// ============================================================================
// EFFECT TYPES & CONFIGURATION
// ============================================================================

const EFFECT_CONFIG = {
  victory: {
    duration: 500,
    color: 'rgba(251, 191, 36, 0.3)', // Gold
    borderColor: 'rgba(251, 191, 36, 0.8)',
    className: 'combat-effect-victory'
  },
  defeat: {
    duration: 600,
    color: 'rgba(239, 68, 68, 0.25)', // Red
    borderColor: 'rgba(239, 68, 68, 0.6)',
    className: 'combat-effect-defeat'
  },
  territoryGained: {
    duration: 400,
    color: 'rgba(34, 197, 94, 0.3)', // Green
    borderColor: 'rgba(34, 197, 94, 0.8)',
    className: 'combat-effect-gained'
  },
  territoryLost: {
    duration: 500,
    color: 'rgba(239, 68, 68, 0.35)',
    borderColor: 'rgba(239, 68, 68, 0.7)',
    className: 'combat-effect-lost'
  }
}

// ============================================================================
// SCREEN FLASH EFFECT COMPONENT
// ============================================================================

function ScreenFlash({ type, onComplete }) {
  const [isActive, setIsActive] = useState(true)
  const config = EFFECT_CONFIG[type] || EFFECT_CONFIG.victory

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsActive(false)
      onComplete?.()
    }, config.duration)

    return () => clearTimeout(timer)
  }, [config.duration, onComplete])

  // Check for reduced motion preference
  const prefersReducedMotion = typeof window !== 'undefined'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false

  if (!isActive || prefersReducedMotion) return null

  return createPortal(
    <div 
      className={`fixed inset-0 z-30 pointer-events-none ${config.className}`}
      style={{
        animation: `combat-flash ${config.duration}ms ease-out forwards`
      }}
    >
      {/* Vignette effect for defeat */}
      {type === 'defeat' && (
        <div 
          className="absolute inset-0"
          style={{
            background: `radial-gradient(ellipse at center, transparent 30%, ${config.color} 100%)`,
            animation: `vignette-pulse ${config.duration}ms ease-out forwards`
          }}
        />
      )}
      
      {/* Pulse effect for victory */}
      {type === 'victory' && (
        <div 
          className="absolute inset-0"
          style={{
            background: `radial-gradient(ellipse at center, ${config.color} 0%, transparent 70%)`,
            animation: `victory-pulse ${config.duration}ms ease-out forwards`
          }}
        />
      )}

      {/* Border flash for territory changes */}
      {(type === 'territoryGained' || type === 'territoryLost') && (
        <div 
          className="absolute inset-2 rounded-lg"
          style={{
            boxShadow: `inset 0 0 60px ${config.color}, 0 0 30px ${config.color}`,
            border: `2px solid ${config.borderColor}`,
            animation: `border-flash ${config.duration}ms ease-out forwards`
          }}
        />
      )}
    </div>,
    document.body
  )
}

// ============================================================================
// VICTORY BANNER COMPONENT
// ============================================================================

function VictoryBanner({ message, onComplete }) {
  const [isVisible, setIsVisible] = useState(true)

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false)
      onComplete?.()
    }, 1500)

    return () => clearTimeout(timer)
  }, [onComplete])

  // Check for reduced motion preference
  const prefersReducedMotion = typeof window !== 'undefined'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false

  if (!isVisible) return null

  return createPortal(
    <div 
      className="fixed top-1/3 left-1/2 -translate-x-1/2 z-40 pointer-events-none"
      style={{
        animation: prefersReducedMotion 
          ? 'none' 
          : 'banner-in 0.3s ease-out, banner-out 0.3s ease-in 1.2s forwards'
      }}
    >
      <div className="bg-gradient-to-r from-yellow-600/90 via-yellow-500/90 to-yellow-600/90 backdrop-blur-sm px-8 py-4 rounded-lg shadow-2xl border border-yellow-400/50">
        <div className="flex items-center gap-3">
          <span className="text-3xl">🏆</span>
          <span className="text-xl font-bold text-white drop-shadow-lg">{message}</span>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ============================================================================
// MAIN COMBAT FEEDBACK HOOK
// ============================================================================

export function useCombatFeedback() {
  const [activeEffects, setActiveEffects] = useState([])
  const effectIdRef = useRef(0)

  const showEffect = useCallback((type, message = null) => {
    const id = ++effectIdRef.current
    setActiveEffects(prev => [...prev, { id, type, message }])
  }, [])

  const removeEffect = useCallback((id) => {
    setActiveEffects(prev => prev.filter(e => e.id !== id))
  }, [])

  const showVictory = useCallback((message = 'Victory!') => {
    showEffect('victory', message)
  }, [showEffect])

  const showDefeat = useCallback((message = 'Defeat') => {
    showEffect('defeat', message)
  }, [showEffect])

  const showTerritoryGained = useCallback((countryName) => {
    showEffect('territoryGained', `${countryName} captured!`)
  }, [showEffect])

  const showTerritoryLost = useCallback((countryName) => {
    showEffect('territoryLost', `${countryName} lost!`)
  }, [showEffect])

  // Component to render effects
  const CombatFeedbackEffects = useCallback(() => (
    <>
      {activeEffects.map(effect => (
        <ScreenFlash
          key={effect.id}
          type={effect.type}
          onComplete={() => removeEffect(effect.id)}
        />
      ))}
      {activeEffects
        .filter(e => e.type === 'victory' && e.message)
        .map(effect => (
          <VictoryBanner
            key={`banner-${effect.id}`}
            message={effect.message}
            onComplete={() => {}}
          />
        ))}
    </>
  ), [activeEffects, removeEffect])

  return {
    showVictory,
    showDefeat,
    showTerritoryGained,
    showTerritoryLost,
    CombatFeedbackEffects
  }
}

// ============================================================================
// CSS KEYFRAMES (to be added to index.css)
// ============================================================================

/*
@keyframes combat-flash {
  0% { opacity: 0; }
  20% { opacity: 1; }
  100% { opacity: 0; }
}

@keyframes victory-pulse {
  0% { opacity: 0; transform: scale(0.8); }
  30% { opacity: 1; transform: scale(1); }
  100% { opacity: 0; transform: scale(1.1); }
}

@keyframes vignette-pulse {
  0% { opacity: 0; }
  30% { opacity: 1; }
  100% { opacity: 0; }
}

@keyframes border-flash {
  0% { opacity: 0; transform: scale(0.98); }
  30% { opacity: 1; transform: scale(1); }
  100% { opacity: 0; transform: scale(1.01); }
}

@keyframes banner-in {
  0% { opacity: 0; transform: translateX(-50%) translateY(-20px) scale(0.9); }
  100% { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
}

@keyframes banner-out {
  0% { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
  100% { opacity: 0; transform: translateX(-50%) translateY(-10px) scale(0.95); }
}
*/

export default function CombatFeedback({ effects = [] }) {
  // This is a simple wrapper for direct usage
  // Prefer using the useCombatFeedback hook for programmatic control
  return (
    <>
      {effects.map((effect, idx) => (
        <ScreenFlash key={idx} type={effect.type} />
      ))}
    </>
  )
}
